// /resources/js/store/CartStore.js

import {defineStore} from 'pinia'
import {getLocalStorage, setLocalStorage, checkDocument, scrollTop} from '@js/helpers.ts'
import {useThemeStore} from "@js/store/ThemeStore.js"
import {ecomAdd, ecomRemove, ecomPurchase, mapProduct} from '@js/ecommerce.js'
import axios from "axios"

const COOKIE_CART = 'cart_metal_v2'
const COOKIE_UI = 'cart_ui_metal_v2'
const YEAR_IN_MINUTES = 525600

function roundMoney(v) {
    return Math.round((Number(v) + Number.EPSILON) * 100) / 100
}

function roundTons(v) {
    return Number(Number(v).toFixed(3))
}

export const useCartStore = defineStore('cartStore', {
    state: () => ({
        catalog: {},
        ui: {},
        items: [],
        loadings: {
            cart_loading: true,
            sending: false,
        },
        isCartClearModalVisible: false,
        cart_products: [],

        form: {
            full_name: '',
            phone: '',
            email: '',
            inn: '',
            comment: '',
            captcha_token: '',

            delivery_type: 'self_delivery',
            delivery_address: '',

            payment_type: 'cash',
        },

        files: [],
        file_previews: [],

        cart_form_successfully_sent: false,
        form_errors: [],
    }),

    getters: {
        productOf: (s) => (id) => s.catalog[id] ?? null,

        uiOf: (s) => (id) => s.ui[id] ?? {mode: 't', qty: 0.1},

        step() {
            return () => 0.1
        },

        minQty() {
            return () => 0.1
        },

        priceActive() {
            return (id) => {
                const p = this.productOf(id)
                if (!p) return 0
                return Number(p.price || 0)
            }
        },

        priceOld() {
            return () => null
        },

        hasDiscount() {
            return () => false
        },

        discountPercent() {
            return () => null
        },

        priceRows() {
            return () => ([
                {label: '/ т', price: 0, old: null, discount: null},
            ])
        },

        weightKg() {
            return () => null
        },

        lengthM() {
            return () => null
        },

        totalPrice() {
            return (id) => roundMoney(this.priceActive(id) * Number(this.uiOf(id).qty || 0))
        },

        unitLabel() {
            return () => 'т'
        },

        isAvailable() {
            return (id) => !!this.productOf(id)?.available
        },

        findItem: (s) => (id, mode = 't') => s.items.find(i => i.id === Number(id) && i.mode === mode),

        isInCartExact() {
            return (id) => !!this.findItem(id, 't')
        },

        isInCartAny() {
            return (id) => !!this.findItem(id, 't')
        },

        canAddToCart() {
            return (id) => this.isAvailable(id) && this.priceActive(id) > 0
        },

        subTotal(state) {
            return roundMoney(state.items.reduce((sum, it) => {
                const p = this.productOf(it.id)
                if (!p) return sum
                const price = Number(p.price || 0)
                return sum + price * Number(it.qty || 0)
            }, 0))
        },

        weightTotal() {
            return null
        },
    },

    actions: {
        _mapForEcom(id, qty, list = 'Cart') {
            const p = this.productOf(id)
            if (!p) return null
            const mp = mapProduct(p, {list, position: 1, qty: Number(qty || 1)})
            mp.variant = 'т'
            return mp
        },

        _trackCartDelta(id, prevQty, nextQty) {
            const p = this.productOf(id)
            if (!p || prevQty == null || nextQty == null) return

            const diff = Number(nextQty) - Number(prevQty)
            if (diff === 0) return

            const qtyAbs = Math.abs(diff)
            const mp = this._mapForEcom(id, qtyAbs, 'Cart')
            if (!mp) return

            try {
                if (diff > 0) ecomAdd(mp)
                else ecomRemove(mp)
            } catch {
            }
        },

        hydrate() {
            this.loadCart()
            this.loadUI()
            this._persistUIFiltered()
        },

        registerCatalogProduct(product, defaultMode = 't') {
            const id = Number(product.id)

            this.catalog[id] = {
                id: product.id,
                title: product.title,
                slug: product.slug,
                category_slug: product.category_slug,
                price: product.price,
                available: !!product.available,
                images: product.images ?? null,
            }

            if (!this.ui[id]) {
                this.ui[id] = {mode: 't', qty: 0.1}
            }
        },

        loadUI() {
            const raw = getLocalStorage(COOKIE_UI) || '{}'
            try {
                const obj = JSON.parse(raw)
                this.ui = (obj && typeof obj === 'object') ? obj : {}
            } catch {
                this.ui = {}
            }

            for (const [k, v] of Object.entries(this.ui)) {
                const id = Number(k)
                const qty = v?.qty ?? 0.1
                this.ui[id] = {mode: 't', qty: roundTons(Math.max(0.1, Number(qty) || 0.1))}
            }
        },

        _persistUIFiltered() {
            const tracked = new Set(this.items.map(i => i.id))
            const toSave = {}
            for (const [k, v] of Object.entries(this.ui)) {
                const id = Number(k)
                if (tracked.has(id)) toSave[k] = {mode: 't', qty: v?.qty ?? 0.1}
            }
            setLocalStorage(COOKIE_UI, JSON.stringify(toSave), YEAR_IN_MINUTES)
        },

        _syncCartLineIfExists(id) {
            const {qty} = this.uiOf(id)
            const line = this.findItem(id, 't')
            if (!line) return

            line.qty = roundTons(qty)

            if (line.qty <= 0) this.remove(id, 't')
            this.persistCart()
            this._persistUIFiltered()
        },

        setMode(id, next) {
            const ui = this.ui[id] ?? {mode: 't', qty: 0.1}
            ui.mode = 't'
            ui.qty = roundTons(Math.max(0.1, Number(ui.qty) || 0.1))
            this.ui[id] = ui
            this._syncCartLineIfExists(Number(id))
        },

        incQty(id) {
            id = Number(id)
            const ui = this.ui[id]
            if (!ui) return

            ui.qty = roundTons((Number(ui.qty) || 0) + 0.1)
            this._syncCartLineIfExists(id)
        },

        decQty(id) {
            id = Number(id)
            const ui = this.ui[id]
            if (!ui) return

            const min = 0.1
            ui.qty = roundTons(Math.max(min, (Number(ui.qty) || 0) - 0.1))
            this._syncCartLineIfExists(id)
        },

        setQty(id, v) {
            id = Number(id)
            const ui = this.ui[id]
            if (!ui) return

            const num = Number(v)
            if (Number.isNaN(num)) return

            const min = 0.1
            ui.qty = roundTons(Math.max(min, num))
            this._syncCartLineIfExists(id)
        },

        setQtyFromInput(id, raw) {
            this.setQty(id, String(raw).replace(',', '.'))
        },

        loadCart() {
            let raw = getLocalStorage(COOKIE_CART)

            if (!raw) {
                const legacy = getLocalStorage('cart_metal_v1')
                if (legacy) {
                    try {
                        const arr = JSON.parse(legacy)
                        if (Array.isArray(arr)) {
                            const migrated = arr
                                .filter(r => r && r.id != null)
                                .map(r => ({
                                    id: Number(r.id),
                                    mode: 't',
                                    qty: roundTons(Math.max(0.1, Number(r.qty) || 0.1)),
                                }))
                            raw = JSON.stringify(migrated)
                            setLocalStorage(COOKIE_CART, raw, YEAR_IN_MINUTES)
                        }
                    } catch {
                    }
                }
            }

            raw = raw || '[]'

            try {
                const arr = JSON.parse(raw)
                this.items = Array.isArray(arr)
                    ? arr
                        .filter(r => r && r.id != null)
                        .map(r => ({
                            id: Number(r.id),
                            mode: 't',
                            qty: roundTons(Math.max(0.1, Number(r.qty) || 0.1)),
                        }))
                    : []
            } catch {
                this.items = []
            }
        },

        getCartData() {
            this.loadings.cart_loading = true
            axios.post('/axios/get-products-by-ids', {ids: this.items.map(i => i.id) ?? []})
                .then(response => {
                    this.cart_products = response.data.products ?? []
                    this.loadings.cart_loading = false
                })
                .catch(() => {
                    this.loadings.cart_loading = false
                })
        },

        persistCart() {
            setLocalStorage(COOKIE_CART, JSON.stringify(this.items), YEAR_IN_MINUTES)
        },

        addToCartFromCard(id) {
            id = Number(id)
            if (!this.canAddToCart(id)) return

            const qty = roundTons(this.uiOf(id).qty)

            const existing = this.findItem(id, 't')
            if (existing) {
                const prev = existing.qty
                existing.qty = roundTons(existing.qty + qty)

                const mp = this._mapForEcom(id, (existing.qty - prev), 'Cart')
                if (mp) {
                    try {
                        ecomAdd(mp)
                    } catch {
                    }
                }
            } else {
                this.items.push({id, mode: 't', qty})
                const mp = this._mapForEcom(id, qty, 'Cart')
                if (mp) {
                    try {
                        ecomAdd(mp)
                    } catch {
                    }
                }
            }

            this.persistCart()
            this._persistUIFiltered()

            const themeStore = useThemeStore()
            themeStore.addToast("Добавлено в заявку", "success", {route_name: 'cart'}, 5000)
        },

        updateItemQty(id, mode, qty) {
            const it = this.findItem(Number(id), 't')
            if (!it) return

            const prev = it.qty
            it.qty = roundTons(Math.max(0.1, Number(qty) || 0.1))

            if (it.qty <= 0) {
                const mp = this._mapForEcom(Number(id), prev, 'Cart')
                if (mp) {
                    try {
                        ecomRemove(mp)
                    } catch {
                    }
                }
                this.remove(id, 't')
                return
            }

            this._trackCartDelta(Number(id), prev, it.qty)
            this.persistCart()
            this._persistUIFiltered()
        },

        remove(id, mode = 't') {
            const it = this.findItem(Number(id), 't')
            if (it) {
                const mp = this._mapForEcom(Number(id), it.qty, 'Cart')
                if (mp) {
                    try {
                        ecomRemove(mp)
                    } catch {
                    }
                }
            }

            this.items = this.items.filter(i => !(i.id === Number(id) && i.mode === 't'))
            this.persistCart()

            const stillHas = this.items.some(i => i.id === Number(id))
            if (!stillHas) {
                delete this.ui[id]
            }

            this._persistUIFiltered()
            this.getCartData()
        },

        clear() {
            try {
                for (const it of this.items) {
                    const mp = this._mapForEcom(it.id, it.qty, 'Cart')
                    if (mp) ecomRemove(mp)
                }
            } catch {
            }

            this.items = []
            this.persistCart()
            this._persistUIFiltered()
            this.getCartData()
        },

        clearFiles() {
            this.file_previews.forEach((url) => {
                if (!url) return
                try {
                    URL.revokeObjectURL(url)
                } catch {
                }
            })
            this.files = []
            this.file_previews = []
        },

        setFiles(fileList) {
            if (!fileList?.length) return

            const incoming = Array.from(fileList)
            const imageFiles = incoming.filter(file => file.type?.startsWith('image/'))

            this.files = [...this.files, ...imageFiles]
            this.file_previews = [
                ...this.file_previews,
                ...imageFiles.map(file => URL.createObjectURL(file))
            ]
        },

        removeFile(index) {
            const preview = this.file_previews[index]
            if (preview) {
                try {
                    URL.revokeObjectURL(preview)
                } catch {
                }
            }

            this.files.splice(index, 1)
            this.file_previews.splice(index, 1)
        },

        resetFormState() {
            this.form.full_name = ''
            this.form.phone = ''
            this.form.email = ''
            this.form.inn = ''
            this.form.comment = ''
            this.form.captcha_token = ''
            this.form.delivery_type = 'self_delivery'
            this.form.delivery_address = ''
            this.form.payment_type = 'cash'
            this.form_errors = []
            this.clearFiles()
        },

        sendForm() {
            if (!checkDocument()) return

            this.form_errors = []
            this.loadings.sending = false
            this.cart_form_successfully_sent = false
            this.form.captcha_token = ''

            const captchaId = 'smart-captcha-container-cart-form'

            const existing = document.getElementById(captchaId)
            if (existing) {
                try {
                    window.smartCaptcha.destroy(existing.dataset.widgetId)
                } catch (e) {
                }
                existing.remove()
            }

            const container = document.createElement('div')
            container.id = captchaId
            container.style.position = 'absolute'
            container.style.zIndex = '-1'
            container.style.left = '-9999px'
            container.style.top = '-9999px'
            document.body.appendChild(container)

            let tokenReceived = false

            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true
                    this.loadings.sending = true
                    this.form.captcha_token = token

                    const formData = new FormData()
                    formData.append('bitrix_title', 'Заявка на сдачу металлолома')
                    formData.append('full_name', this.form.full_name || '')
                    formData.append('phone', this.form.phone || '')
                    formData.append('email', this.form.email || '')
                    formData.append('inn', this.form.inn || '')
                    formData.append('comment', this.form.comment || '')
                    formData.append('captcha_token', this.form.captcha_token || '')
                    formData.append('delivery_type', this.form.delivery_type || '')
                    formData.append('delivery_address', this.form.delivery_address || '')
                    formData.append('payment_type', this.form.payment_type || '')
                    formData.append('cart_products', JSON.stringify(this.items ?? []))

                    this.files.forEach((file, index) => {
                        formData.append(`files[${index}]`, file)
                    })

                    axios.post('/axios/form/send-lead', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then(response => {
                        scrollTop()
                        this.loadings.sending = false
                        this.cart_form_successfully_sent = true

                        try {
                            const orderId = response?.data?.order_id ?? null
                            const revenue = typeof response?.data?.revenue === 'number' ? response.data.revenue : undefined

                            if (orderId) {
                                const products = this.items.map((it, idx) => {
                                    const p = this.productOf(it.id)
                                    const mp = mapProduct(p, {
                                        list: 'Purchase',
                                        position: idx + 1,
                                        qty: Number(it.qty || 1),
                                    })
                                    mp.variant = 'т'
                                    return mp
                                })
                                ecomPurchase({orderId, revenue, products})
                            }
                        } catch {
                        }

                        this.clear()
                        this.resetFormState()

                        try {
                            window.smartCaptcha.destroy(widgetId)
                        } catch (e) {
                        }
                    }).catch(error => {
                        this.loadings.sending = false
                        if (error.response?.status === 422) {
                            this.form_errors = error.response.data.errors
                        }
                    })
                }
            })

            document.getElementById(captchaId).dataset.widgetId = widgetId

            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Вы закрыли капчу. Повторите попытку.']
                    }
                    try {
                        window.smartCaptcha.reset(widgetId)
                    } catch (e) {
                    }
                }
            })

            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId)
                } catch (e) {
                    this.loadings.sending = false
                    this.form_errors = {
                        captcha_token: ['Не удалось запустить капчу. Попробуйте снова.']
                    }
                }
            }, 100)
        },
    }
})
